import React, { useState, useCallback } from 'react';
import type { FC } from 'react';
import { Login, ApiKeyLogin } from '@wix/cli-auth';
import type { UserInfo } from '@wix/cli-auth';
import { Text, useExit } from '@wix/cli-ui-kit';
import { useErrorReporter } from '@wix/cli-error-reporting';
import { useBiLogger } from '@wix/cli-telemetry-react';
import type { VariantOf } from 'variant';
import { fields, match, variant } from 'variant';
import { addUserFields } from '../../bi.js';
import { AlreadyLoggedIn } from './AlreadyLoggedIn.js';

interface Props {
  apiKeyToken?: string;
  userInfo: UserInfo | null;
}

const LoginState = variant({
  PendingUserConfirmation: {},
  InProgress: {},
  Success: fields<{ userInfo: UserInfo }>(),
});
type LoginState = VariantOf<typeof LoginState>;

export const LoginCommand: FC<Props> = ({
  apiKeyToken,
  userInfo: initialUserInfo,
}) => {
  const exit = useExit();
  const errorReporter = useErrorReporter();
  const biLogger = useBiLogger();
  const [loginState, setLoginState] = useState<LoginState>(
    initialUserInfo == null
      ? LoginState.InProgress()
      : LoginState.PendingUserConfirmation()
  );

  const handleLogin = useCallback(
    ({ userInfo }: { userInfo: UserInfo }) => {
      errorReporter.setUser({ id: userInfo.userId });
      addUserFields(biLogger, userInfo.userId);
      setLoginState(LoginState.Success({ userInfo }));
    },
    [setLoginState, errorReporter, biLogger]
  );

  return (
    <>
      {initialUserInfo && (
        <AlreadyLoggedIn
          userInfo={initialUserInfo}
          onSubmit={(relogin) =>
            relogin && setLoginState(LoginState.InProgress())
          }
        />
      )}
      {match(loginState, {
        PendingUserConfirmation: () => null,
        InProgress: () =>
          apiKeyToken ? (
            <ApiKeyLogin
              token={apiKeyToken}
              onLogin={handleLogin}
              onError={exit}
            />
          ) : (
            <Login onLogin={handleLogin} onError={exit} />
          ),
        Success: ({ userInfo }) => (
          <Text>
            Wix CLI is logged in to account{' '}
            <Text skin="info">{userInfo.email}</Text>
          </Text>
        ),
      })}
    </>
  );
};
